<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack" :disabled="submitting">
          取消
        </button>
        <button
          v-if="currentStep > 0"
          type="button"
          class="btn btn-outline-secondary"
          @click="previousStep"
          :disabled="submitting"
        >
          <i class="ri-arrow-left-line"></i>
          上一步
        </button>
        <button
          v-if="currentStep < steps.length - 1"
          type="button"
          class="btn btn-primary"
          @click="nextStep"
          :disabled="submitting || !canProceedToNextStep"
        >
          下一步
          <i class="ri-arrow-right-line"></i>
        </button>
        <button
          v-if="currentStep === steps.length - 1"
          type="button"
          class="btn btn-primary"
          :disabled="submitting || (!useFilterMode && selectedMembers.length === 0) || !form.points"
          @click="handleSubmit"
        >
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            發放中...
          </span>
          <span v-else>
            <i class="ri-gift-line"></i>
            確認批次發放（{{ totalPoints }} 點）
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-if="!loading && !error && isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>批次發放將逐筆呼叫 Wixtar API 入點（以 Wixtar 為準）；CRM 發放紀錄與會員餘額為同步後副本。
      票券／優惠仍以 Wixtar 後台與 API 為主，本流程僅處理點數。
    </div>

    <div v-if="!loading && !error" class="form-container">
      <!-- 步驟導航 -->
      <div class="stepper">
        <div class="stepper-header">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step-item"
            :class="{
              active: currentStep === index,
              completed: currentStep > index,
              disabled: currentStep < index
            }"
          >
            <div class="step-number">
              <span v-if="currentStep > index" class="step-check">
                <i class="ri-check-line"></i>
              </span>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
        <div class="stepper-line">
          <div class="stepper-progress" :style="{ width: `${(currentStep / (steps.length - 1)) * 100}%` }"></div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- 步驟一：會員篩選 -->
        <div v-show="currentStep === 0" class="step-content">
          <div class="form-section">
            <h2 class="section-title">步驟一：會員篩選</h2>
          
            <!-- Tab 切換 -->
            <div class="selection-tabs">
              <button
                type="button"
                :class="['tab-button', { active: selectionMode === 'search' }]"
                @click="handleSelectionModeChange('search')"
              >
                <i class="ri-search-line"></i>
                搜尋會員
              </button>
              <button
                type="button"
                :class="['tab-button', { active: selectionMode === 'tags' }]"
                @click="handleSelectionModeChange('tags')"
              >
                <i class="ri-price-tag-3-line"></i>
                標籤搜尋
              </button>
              <button
                type="button"
                :class="['tab-button', { active: selectionMode === 'identifiers' }]"
                @click="handleSelectionModeChange('identifiers')"
              >
                <i class="ri-file-list-3-line"></i>
                批量輸入
              </button>
              <button
                type="button"
                :class="['tab-button', { active: selectionMode === 'filter' }]"
                @click="handleSelectionModeChange('filter')"
              >
                <i class="ri-filter-3-line"></i>
                篩選條件
              </button>
              <button
                type="button"
                :class="['tab-button', { active: selectionMode === 'csv' }]"
                @click="handleSelectionModeChange('csv')"
              >
                <i class="ri-file-upload-line"></i>
                匯入會員資料
              </button>
            </div>

            <!-- 搜尋方式 -->
            <div v-if="selectionMode === 'search'" class="selection-content">
              <div class="form-group">
                <label class="form-label">搜尋會員</label>
                <div class="member-search">
                  <input
                    v-model="memberSearchQuery"
                    type="text"
                    class="form-control"
                    placeholder="輸入會員名稱、電話或 Email 搜尋..."
                    @input="handleMemberSearch"
                  />
                  <i class="ri-search-line"></i>
                </div>
                <small class="form-text">輸入會員名稱、電話或 Email 進行搜尋，然後點擊會員加入批次發放列表</small>
              </div>

              <!-- 會員搜尋結果 -->
              <div v-if="memberSearchQuery && memberSearchResults.length > 0" class="member-results">
                <div class="results-header">
                  <span>找到 {{ memberSearchResults.length }} 位會員</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-link"
                    @click="addAllMembers"
                    :disabled="addingAll"
                  >
                    <i class="ri-add-line"></i>
                    全部加入
                  </button>
                </div>
                <div class="member-list">
                  <div
                    v-for="member in memberSearchResults"
                    :key="member.id"
                    class="member-item"
                    :class="{ selected: isMemberSelected(member.id) }"
                    @click="toggleMember(member)"
                  >
                    <div class="member-info">
                      <div class="member-name">{{ member.name }}</div>
                      <div class="member-details">
                        <span v-if="member.phone">{{ member.phone }}</span>
                        <span v-if="member.email">{{ member.email }}</span>
                      </div>
                    </div>
                    <div v-if="isMemberSelected(member.id)" class="check-icon">
                      <i class="ri-check-line"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="memberSearchQuery && !memberSearching && memberSearchResults.length === 0" class="no-results">
                <i class="ri-user-search-line"></i>
                <p>找不到符合的會員</p>
              </div>
            </div>

            <!-- 標籤搜尋方式 -->
            <div v-if="selectionMode === 'tags'" class="selection-content">
              <div class="form-group">
                <label class="form-label">選擇標籤</label>
                
                <!-- 標籤搜尋框 -->
                <div v-if="!tagsLoading && availableTags.length > 0" class="tag-search-box">
                  <input
                    v-model="tagSearchQuery"
                    type="text"
                    class="form-control"
                    placeholder="搜尋標籤名稱..."
                    @input="handleTagSearch"
                  />
                  <i class="ri-search-line"></i>
                </div>

                <div v-if="tagsLoading" class="form-text">載入標籤中...</div>
                <div v-else-if="availableTags.length === 0" class="form-text text-muted">目前沒有可用的標籤</div>
                <div v-else-if="filteredTagCategories.length === 0" class="form-text text-muted">找不到符合的標籤</div>
                <div v-else>
                  <div
                    v-for="category in filteredTagCategories"
                    :key="category.key"
                    class="tag-category"
                  >
                    <div class="category-header">
                      <h4 class="category-title">
                        <i :class="category.icon"></i>
                        {{ category.label }}
                      </h4>
                      <span class="category-count">({{ category.tags.length }})</span>
                    </div>
                    <div class="tag-selector">
                      <div
                        v-for="tag in category.tags"
                        :key="tag.id"
                        class="tag-item"
                        :class="{ selected: selectedTagIds.includes(tag.id) }"
                        @click.stop="toggleTag(tag.id)"
                      >
                        <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
                        <span>{{ tag.tagName }}</span>
                        <div v-if="selectedTagIds.includes(tag.id)" class="check-icon">
                          <i class="ri-check-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <small class="form-text">選擇一個或多個標籤，系統會找出擁有這些標籤的所有會員</small>
              </div>

              <div v-if="selectedTagIds.length > 0" class="form-group">
                <TagMatchTypeToggle v-model="tagsTabMatchType" label="標籤符合邏輯" />
              </div>

              <div v-if="selectedTagIds.length > 0" class="form-group">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="previewMembersByTags"
                  :disabled="tagPreviewing"
                >
                  <span v-if="tagPreviewing">
                    <i class="ri-loader-4-line spin"></i>
                    預覽中...
                  </span>
                  <span v-else>
                    <i class="ri-eye-line"></i>
                    預覽符合標籤的會員
                  </span>
                </button>
              </div>

              <!-- 標籤預覽結果 -->
              <div v-if="tagPreviewResult" class="preview-result-card">
                <div class="preview-header">
                  <h4>預覽結果</h4>
                  <span class="preview-count">符合標籤：{{ tagPreviewResult.estimatedMemberCount }} 位會員</span>
                </div>
                <div v-if="tagPreviewResult.previewMembers && tagPreviewResult.previewMembers.length > 0" class="preview-members-table">
                  <table>
                    <thead>
                      <tr>
                        <th>姓名</th>
                        <th>電話</th>
                        <th>等級</th>
                        <th>累積消費</th>
                        <th>消費次數</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="member in tagPreviewResult.previewMembers" :key="member.id">
                        <td>{{ member.name }}</td>
                        <td>{{ member.phone }}</td>
                        <td>{{ member.memberTier || '-' }}</td>
                        <td>${{ formatNumber(member.totalSpent) }}</td>
                        <td>{{ member.purchaseCount }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="tagPreviewResult.estimatedMemberCount > 10" class="preview-note">
                    <small>僅顯示前 10 筆，共 {{ tagPreviewResult.estimatedMemberCount }} 位會員</small>
                  </div>
                </div>
                <div v-else class="preview-empty">
                  <i class="ri-inbox-line"></i>
                  <p>沒有符合條件的會員</p>
                </div>
                <div class="preview-actions">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="useTagFilteredMembers"
                    :disabled="!tagPreviewResult || tagPreviewResult.estimatedMemberCount === 0"
                  >
                    <i class="ri-check-line"></i>
                    使用此標籤條件發放（{{ tagPreviewResult?.estimatedMemberCount || 0 }} 位會員）
                  </button>
                </div>
              </div>
            </div>

            <!-- 批量輸入方式 -->
            <div v-if="selectionMode === 'identifiers'" class="selection-content">
              <div class="form-group">
                <label class="form-label">批量輸入識別資訊</label>
                <textarea
                  v-model="identifiersText"
                  class="form-control"
                  rows="8"
                  placeholder="每行一個或使用逗號分隔：&#10;• 會員 ID（UUID）&#10;• 電話號碼（09xxxxxxxx）&#10;• LINE User ID&#10;&#10;例如：&#10;0901234567&#10;0912345678&#10;U1234567890abcdefghijklmnopqrstuv"
                  @input="handleIdentifiersInput"
                ></textarea>
                <small class="form-text">支援換行或逗號分隔，最多 10,000 筆</small>
              </div>

              <div class="form-group">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="previewMembersByIdentifiers"
                  :disabled="identifiersPreviewing || !identifiersText.trim()"
                >
                  <span v-if="identifiersPreviewing">
                    <i class="ri-loader-4-line spin"></i>
                    預覽中...
                  </span>
                  <span v-else>
                    <i class="ri-eye-line"></i>
                    預覽符合的會員
                  </span>
                </button>
              </div>

              <!-- 批量輸入預覽結果 -->
              <div v-if="identifiersPreviewResult" class="preview-result-card">
                <div class="preview-header">
                  <h4>預覽結果</h4>
                  <span class="preview-count">符合名單：{{ identifiersPreviewResult.estimatedMemberCount }} 位會員</span>
                </div>
                <div v-if="identifiersPreviewResult.previewMembers && identifiersPreviewResult.previewMembers.length > 0" class="preview-members-table">
                  <table>
                    <thead>
                      <tr>
                        <th>姓名</th>
                        <th>電話</th>
                        <th>等級</th>
                        <th>累積消費</th>
                        <th>消費次數</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="member in identifiersPreviewResult.previewMembers" :key="member.id">
                        <td>{{ member.name }}</td>
                        <td>{{ member.phone }}</td>
                        <td>{{ member.memberTier || '-' }}</td>
                        <td>${{ formatNumber(member.totalSpent) }}</td>
                        <td>{{ member.purchaseCount }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="identifiersPreviewResult.estimatedMemberCount > 10" class="preview-note">
                    <small>僅顯示前 10 筆，共 {{ identifiersPreviewResult.estimatedMemberCount }} 位會員</small>
                  </div>
                </div>
                <div v-else class="preview-empty">
                  <i class="ri-inbox-line"></i>
                  <p>名單中沒有找到符合的會員</p>
                </div>
                <div class="preview-actions">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="useIdentifiersMembers"
                    :disabled="!identifiersPreviewResult || identifiersPreviewResult.estimatedMemberCount === 0"
                  >
                    <i class="ri-check-line"></i>
                    使用此名單發放（{{ identifiersPreviewResult?.estimatedMemberCount || 0 }} 位會員）
                  </button>
                </div>
              </div>
            </div>

            <!-- 篩選條件方式 -->
            <div v-if="selectionMode === 'filter'" class="selection-content">
              <!-- 複製優惠券頁面的篩選條件區塊，但移除 excludeAlreadyIssued（點數不需要） -->
              <div class="filter-sections">
                <!-- 標籤篩選 -->
                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('tags')">
                    <h4 class="filter-section-title">
                      <i class="ri-price-tag-3-line"></i>
                      標籤篩選
                    </h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.tags }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.tags" class="filter-section-body">
                    <div class="form-group">
                      <TagMatchTypeToggle v-model="filterConfig.tagLogic" />
                    </div>
                    <div class="form-group">
                      <label class="form-label">選擇標籤</label>
                      <div v-if="!tagsLoading && availableTags.length > 0" class="tag-search-box">
                        <input v-model="tagSearchQuery" type="text" class="form-control" placeholder="搜尋標籤名稱..." @input="handleTagSearch" />
                        <i class="ri-search-line"></i>
                      </div>
                      <div v-if="tagsLoading" class="form-text">載入標籤中...</div>
                      <div v-else-if="availableTags.length === 0" class="form-text text-muted">目前沒有可用的標籤</div>
                      <div v-else-if="filteredTagCategories.length === 0" class="form-text text-muted">找不到符合的標籤</div>
                      <div v-else>
                        <div v-for="category in filteredTagCategories" :key="category.key" class="tag-category">
                          <div class="category-header">
                            <h4 class="category-title"><i :class="category.icon"></i> {{ category.label }}</h4>
                            <span class="category-count">({{ category.tags.length }})</span>
                          </div>
                          <div class="tag-selector">
                            <div v-for="tag in category.tags" :key="tag.id" class="tag-item" :class="{ selected: filterConfig.tags.includes(tag.id) }" @click.stop="toggleFilterTag(tag.id)">
                              <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
                              <span>{{ tag.tagName }}</span>
                              <div v-if="filterConfig.tags.includes(tag.id)" class="check-icon"><i class="ri-check-line"></i></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="filterConfig.tags.length > 0" class="form-group">
                      <label class="form-label">排除標籤</label>
                      <div class="tag-selector">
                        <div v-for="tag in availableTags.filter(t => !filterConfig.tags.includes(t.id))" :key="tag.id" class="tag-item" :class="{ selected: filterConfig.excludeTags.includes(tag.id) }" @click.stop="toggleExcludeTag(tag.id)">
                          <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
                          <span>{{ tag.tagName }}</span>
                          <div v-if="filterConfig.excludeTags.includes(tag.id)" class="check-icon"><i class="ri-check-line"></i></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 會員等級篩選 -->
                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('tiers')">
                    <h4 class="filter-section-title"><i class="ri-vip-crown-line"></i> 會員等級篩選</h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.tiers }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.tiers" class="filter-section-body">
                    <div class="form-group">
                      <label class="form-label">選擇會員等級</label>
                      <div v-if="tiersLoading" class="form-text">載入等級中...</div>
                      <div v-else class="tier-selector">
                        <div v-for="tier in availableTiers" :key="tier.id" class="tier-item" :class="{ selected: filterConfig.tiers.includes(tier.id) }" @click="toggleFilterTier(tier.id)">
                          <span>{{ tier.tierName }}</span>
                          <div v-if="filterConfig.tiers.includes(tier.id)" class="check-icon"><i class="ri-check-line"></i></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- RFM、消費行為、時間、排除條件區塊（簡化版，與優惠券頁面相同） -->
                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('rfm')">
                    <h4 class="filter-section-title"><i class="ri-bar-chart-line"></i> RFM 動態狀態篩選</h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.rfm }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.rfm" class="filter-section-body">
                    <div class="form-group">
                      <label class="form-label">RFM 分群</label>
                      <select v-model="filterConfig.rfmSegments" multiple class="form-control" size="6">
                        <option value="champions">冠軍客戶</option>
                        <option value="loyal_customers">忠誠客戶</option>
                        <option value="potential_loyalists">潛力忠誠客戶</option>
                        <option value="new_customers">新客戶</option>
                        <option value="promising">有潛力客戶</option>
                        <option value="needs_attention">需要關注</option>
                        <option value="about_to_sleep">即將沉睡</option>
                        <option value="cannot_lose_them">不能失去</option>
                        <option value="at_risk">有風險</option>
                        <option value="hibernating">休眠客戶</option>
                        <option value="lost">流失客戶</option>
                      </select>
                      <small class="form-text">可多選，按住 Ctrl (Windows) 或 Cmd (Mac) 進行多選</small>
                    </div>
                    <div class="form-group">
                      <label class="form-label">R 分數範圍（最近購買）</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.rScore[0]" type="number" class="form-control" placeholder="最小值" min="1" max="5" />
                        <span>~</span>
                        <input v-model.number="filterConfig.rScore[1]" type="number" class="form-control" placeholder="最大值" min="1" max="5" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">F 分數範圍（購買頻率）</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.fScore[0]" type="number" class="form-control" placeholder="最小值" min="1" max="5" />
                        <span>~</span>
                        <input v-model.number="filterConfig.fScore[1]" type="number" class="form-control" placeholder="最大值" min="1" max="5" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">M 分數範圍（購買金額）</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.mScore[0]" type="number" class="form-control" placeholder="最小值" min="1" max="5" />
                        <span>~</span>
                        <input v-model.number="filterConfig.mScore[1]" type="number" class="form-control" placeholder="最大值" min="1" max="5" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('behavior')">
                    <h4 class="filter-section-title"><i class="ri-shopping-cart-line"></i> 消費行為篩選</h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.behavior }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.behavior" class="filter-section-body">
                    <div class="form-group">
                      <label class="form-label">累積消費金額</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.minTotalSpent" type="number" class="form-control" placeholder="最低金額" min="0" />
                        <span>~</span>
                        <input v-model.number="filterConfig.maxTotalSpent" type="number" class="form-control" placeholder="最高金額" min="0" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">消費次數</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.minPurchaseCount" type="number" class="form-control" placeholder="最低次數" min="0" />
                        <span>~</span>
                        <input v-model.number="filterConfig.maxPurchaseCount" type="number" class="form-control" placeholder="最高次數" min="0" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">年度消費金額</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.minYearlySpent" type="number" class="form-control" placeholder="最低金額" min="0" />
                        <span>~</span>
                        <input v-model.number="filterConfig.maxYearlySpent" type="number" class="form-control" placeholder="最高金額" min="0" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('time')">
                    <h4 class="filter-section-title"><i class="ri-calendar-line"></i> 時間篩選</h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.time }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.time" class="filter-section-body">
                    <div class="form-group">
                      <label class="form-label">註冊日期範圍</label>
                      <div class="date-range-inputs">
                        <input v-model="filterConfig.registrationDateFrom" type="date" class="form-control" placeholder="開始日期" />
                        <span>~</span>
                        <input v-model="filterConfig.registrationDateTo" type="date" class="form-control" placeholder="結束日期" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">最後消費距今天數</label>
                      <div class="range-inputs">
                        <input v-model.number="filterConfig.lastPurchaseDaysMin" type="number" class="form-control" placeholder="最少天數" min="0" />
                        <span>~</span>
                        <input v-model.number="filterConfig.lastPurchaseDaysMax" type="number" class="form-control" placeholder="最多天數" min="0" />
                      </div>
                      <small class="form-text">例如：設定 30~90 表示最後消費距今 30-90 天的會員</small>
                    </div>
                  </div>
                </div>

                <div class="filter-section-card">
                  <div class="filter-section-header" @click="toggleFilterSection('exclusion')">
                    <h4 class="filter-section-title"><i class="ri-close-circle-line"></i> 排除條件</h4>
                    <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.exclusion }]"></i>
                  </div>
                  <div v-if="filterSectionsExpanded.exclusion" class="filter-section-body">
                    <div class="form-group">
                      <label class="form-label">
                        <input v-model="filterConfig.excludeInactive" type="checkbox" />
                        排除未啟用會員
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 預覽按鈕和結果 -->
              <div class="filter-preview-section">
                <div class="form-group">
                  <button type="button" class="btn btn-primary" @click="previewFilteredMembers" :disabled="previewing">
                    <span v-if="previewing"><i class="ri-loader-4-line spin"></i> 預覽中...</span>
                    <span v-else><i class="ri-eye-line"></i> 預覽符合條件的會員</span>
                  </button>
                </div>
                <div v-if="previewResult" class="preview-result-card">
                  <div class="preview-header">
                    <h4>預覽結果</h4>
                    <span class="preview-count">符合條件：{{ previewResult.estimatedMemberCount }} 位會員</span>
                  </div>
                  <div v-if="previewResult.previewMembers && previewResult.previewMembers.length > 0" class="preview-members-table">
                    <table>
                      <thead>
                        <tr>
                          <th>姓名</th>
                          <th>電話</th>
                          <th>等級</th>
                          <th>累積消費</th>
                          <th>消費次數</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="member in previewResult.previewMembers" :key="member.id">
                          <td>{{ member.name }}</td>
                          <td>{{ member.phone }}</td>
                          <td>{{ member.memberTier || '-' }}</td>
                          <td>${{ formatNumber(member.totalSpent) }}</td>
                          <td>{{ member.purchaseCount }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="previewResult.estimatedMemberCount > 10" class="preview-note">
                      <small>僅顯示前 10 筆，共 {{ previewResult.estimatedMemberCount }} 位會員</small>
                    </div>
                  </div>
                  <div v-else class="preview-empty">
                    <i class="ri-inbox-line"></i>
                    <p>沒有符合條件的會員</p>
                  </div>
                  <div class="preview-actions">
                    <button type="button" class="btn btn-secondary" @click="useFilteredMembers" :disabled="!previewResult || previewResult.estimatedMemberCount === 0">
                      <i class="ri-check-line"></i>
                      使用篩選條件發放（{{ previewResult?.estimatedMemberCount || 0 }} 位會員）
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 匯入會員資料方式 -->
            <div v-if="selectionMode === 'csv'" class="selection-content">
              <MemberCsvUpload
                ref="csvRef"
                v-model="selectedMembers"
              />
            </div>

            <!-- 已選擇的會員列表 -->
            <div v-if="selectedMembers.length > 0" class="selected-members-section">
              <div class="section-header">
                <h3>已選擇的會員（{{ selectedMembers.length }} 位）</h3>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger"
                  @click="clearAllMembers"
                >
                  <i class="ri-delete-bin-line"></i>
                  清除全部
                </button>
              </div>
              <div class="selected-members-list">
                <div
                  v-for="member in selectedMembers"
                  :key="member.id"
                  class="selected-member-item"
                >
                  <div class="member-info">
                    <div class="member-name">{{ member.name }}</div>
                    <div class="member-details">
                      <span v-if="member.phone">{{ member.phone }}</span>
                      <span v-if="member.email">{{ member.email }}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-danger"
                    @click="removeMember(member.id)"
                    title="移除"
                  >
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="errors.memberIds" class="error-text">{{ errors.memberIds }}</div>
          </div>
        </div>

        <!-- 步驟二：設定點數與其他條件 -->
        <div v-show="currentStep === 1" class="step-content">
          <!-- 發放進度追蹤（當使用篩選條件模式且正在發放時顯示） -->
          <div v-if="issuanceProgress && useFilterMode" class="issuance-progress-section">
            <div class="progress-card">
              <div class="progress-header">
                <h3 class="progress-title">
                  <i class="ri-loader-4-line" :class="{ 'spin': issuanceProgress.status === 'processing' }"></i>
                  發放進度
                </h3>
                <span :class="getProgressStatusBadgeClass(issuanceProgress.status)">
                  {{ getProgressStatusText(issuanceProgress.status) }}
                </span>
              </div>
              
              <div class="progress-body">
                <!-- 進度條 -->
                <div class="progress-bar-container">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :style="{ width: `${issuanceProgress.progressPercentage || 0}%` }"
                    ></div>
                  </div>
                  <div class="progress-text">
                    {{ issuanceProgress.progressPercentage || 0 }}% 
                    ({{ issuanceProgress.processedCount || 0 }} / {{ issuanceProgress.targetMemberCount || 0 }})
                  </div>
                </div>

                <!-- 統計資訊 -->
                <div class="progress-stats">
                  <div class="stat-item">
                    <span class="stat-label">目標會員數：</span>
                    <span class="stat-value">{{ issuanceProgress.targetMemberCount || 0 }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">已處理：</span>
                    <span class="stat-value">{{ issuanceProgress.processedCount || 0 }}</span>
                  </div>
                  <div class="stat-item success">
                    <span class="stat-label">成功：</span>
                    <span class="stat-value">{{ issuanceProgress.successCount || 0 }}</span>
                  </div>
                  <div class="stat-item error">
                    <span class="stat-label">失敗：</span>
                    <span class="stat-value">{{ issuanceProgress.failureCount || 0 }}</span>
                  </div>
                  <div v-if="issuanceProgress.totalPointsIssued" class="stat-item">
                    <span class="stat-label">已發放點數：</span>
                    <span class="stat-value">{{ formatNumber(issuanceProgress.totalPointsIssued) }}</span>
                  </div>
                  <div v-if="issuanceProgress.status === 'processing' && issuanceProgress.estimatedTimeRemaining" class="stat-item">
                    <span class="stat-label">預估剩餘時間：</span>
                    <span class="stat-value">{{ formatEstimatedTime(issuanceProgress.estimatedTimeRemaining) }}</span>
                  </div>
                  <div v-if="issuanceProgress.executionTimeMs" class="stat-item">
                    <span class="stat-label">執行時間：</span>
                    <span class="stat-value">{{ formatExecutionTime(issuanceProgress.executionTimeMs) }}</span>
                  </div>
                </div>

                <!-- 完成後的詳細結果 -->
                <div v-if="issuanceProgress.status === 'completed'" class="progress-completed">
                  <div class="success-message">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>發放完成！成功發放給 {{ issuanceProgress.successCount || 0 }} 位會員，共 {{ formatNumber(issuanceProgress.totalPointsIssued || 0) }} 點</span>
                  </div>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="viewIssuanceDetail"
                    >
                      <i class="ri-eye-line"></i>
                      查看詳情
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="resetIssuance"
                    >
                      <i class="ri-refresh-line"></i>
                      重新發放
                    </button>
                  </div>
                </div>

                <!-- 失敗後的錯誤訊息 -->
                <div v-if="issuanceProgress.status === 'failed'" class="progress-failed">
                  <div class="error-message">
                    <i class="ri-error-warning-line"></i>
                    <span>發放失敗：{{ issuanceProgress.errorMessage || '未知錯誤' }}</span>
                  </div>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="viewIssuanceDetail"
                    >
                      <i class="ri-eye-line"></i>
                      查看詳情
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="resetIssuance"
                    >
                      <i class="ri-refresh-line"></i>
                      重新發放
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h2 class="section-title">步驟二：設定點數與其他條件</h2>
            
            <div class="form-group">
              <label class="form-label">點數 <span class="required">*</span></label>
              <input
                v-model.number="form.points"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.points }"
                placeholder="輸入要發放的點數"
                min="1"
                required
              />
              <span v-if="errors.points" class="error-text">{{ errors.points }}</span>
              <small class="form-text">每位會員將獲得此數量的點數</small>
            </div>

            <div class="form-group">
              <label class="form-label">發放原因 <span class="required">*</span></label>
              <textarea
                v-model="form.reason"
                class="form-control"
                rows="3"
                placeholder="請說明發放原因"
                required
                maxlength="200"
              ></textarea>
              <span v-if="errors.reason" class="error-text">{{ errors.reason }}</span>
              <small class="form-text">最多 200 字</small>
            </div>

            <div class="form-group">
              <label class="form-label">生效天數（選填）</label>
              <input
                v-model.number="form.effectiveDays"
                type="number"
                class="form-control"
                placeholder="購買後幾天生效，留空則立即生效"
                min="0"
              />
              <small class="form-text">留空則立即生效</small>
            </div>

            <div class="form-group">
              <label class="form-label">過期日期（選填）</label>
              <input
                v-model="form.expiryDate"
                type="date"
                class="form-control"
              />
              <small class="form-text">留空則使用系統預設過期規則</small>
            </div>

            <!-- 發放統計 -->
            <div class="confirmation-card summary-card">
              <h3 class="card-title">
                <i class="ri-bar-chart-line"></i>
                發放統計
              </h3>
              <div class="issue-summary">
                <div class="summary-item">
                  <span class="label">選擇會員數：</span>
                  <span class="value">{{ selectedMembers.length }} 位</span>
                </div>
                <div class="summary-item">
                  <span class="label">每位發放點數：</span>
                  <span class="value">{{ Number(form.points) || 0 }} 點</span>
                </div>
                <div class="summary-item total">
                  <span class="label">總發放點數：</span>
                  <span class="value">{{ totalPoints }} 點</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 錯誤訊息 -->
        <div v-if="submitError" class="alert alert-danger">
          {{ submitError }}
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { memberService } from '@/api/modules/members.js';
import { tagService } from '@/api/modules/tags.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { manualIssuanceService } from '@/api/modules/manualIssuance.js';
import { showSuccess, showError } from '@/utils/swal.js';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';
import MemberCsvUpload from '@/components/MemberCsvUpload.vue';

export default {
  name: 'PointsBatchIssueCreate',
  components: { TagMatchTypeToggle, MemberCsvUpload },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次發放點數');
    
    const loading = ref(false);
    const error = ref(null);
    const submitting = ref(false);
    const submitError = ref('');
    const selectionMode = ref('search'); // 'search', 'tags', 'identifiers', 'filter'
    
    // 步驟控制
    const currentStep = ref(0);
    const steps = [
      { label: '會員篩選', key: 'members' },
      { label: '設定點數', key: 'points' }
    ];
    
    // 搜尋方式
    const memberSearchQuery = ref('');
    const memberSearchResults = ref([]);
    const memberSearching = ref(false);
    const searchTimeout = ref(null);
    
    // 標籤搜尋方式（預覽模式，不再抓全部會員）
    const availableTags = ref([]);
    const selectedTagIds = ref([]);
    const tagsLoading = ref(false);
    const tagSearchQuery = ref('');
    const tagsTabMatchType = ref('OR');
    const tagPreviewResult = ref(null);
    const tagPreviewing = ref(false);
    
    // 批量輸入方式
    const identifiersText = ref('');
    const identifiersPreviewing = ref(false);
    const identifiersPreviewResult = ref(null);
    const resolvedIdentifierIds = ref([]);
    
    // 篩選條件方式
    const filterConfig = reactive({
      tags: [],
      tagLogic: 'OR',
      excludeTags: [],
      tiers: [],
      rfmSegments: [],
      rScore: [1, 5],
      fScore: [1, 5],
      mScore: [1, 5],
      minTotalSpent: null,
      maxTotalSpent: null,
      minPurchaseCount: null,
      maxPurchaseCount: null,
      minYearlySpent: null,
      maxYearlySpent: null,
      registrationDateFrom: null,
      registrationDateTo: null,
      lastPurchaseDaysMin: null,
      lastPurchaseDaysMax: null,
      excludeInactive: false,
      status: ['active'],
      memberIds: [],
      search: null
    });
    const filterSectionsExpanded = reactive({
      tags: true,
      tiers: false,
      rfm: false,
      behavior: false,
      time: false,
      exclusion: false
    });
    const availableTiers = ref([]);
    const tiersLoading = ref(false);
    const previewResult = ref(null);
    const previewing = ref(false);
    const useFilterMode = ref(false);
    
    // 發放進度追蹤
    const issuanceProgress = ref(null);
    const pollingInterval = ref(null);
    const currentIssuanceId = ref(null);
    
    // 已選擇的會員
    const selectedMembers = ref([]);
    const addingAll = ref(false);
    const isWixtarClient = ref(false);
    const csvRef = ref(null);

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        }
      } catch (e) {
        console.error(e);
      }
    };

    const form = reactive({
      points: null,
      reason: '',
      effectiveDays: null,
      expiryDate: ''
    });

    const errors = reactive({});

    const totalPoints = computed(() => {
      const points = Number(form.points) || 0;
      const memberCount = selectedMembers.value.length || 0;
      const total = points * memberCount;
      return isNaN(total) ? 0 : total;
    });

    // 是否可以進入下一步
    const canProceedToNextStep = computed(() => {
      if (currentStep.value === 0) {
        // 標籤模式或篩選條件模式：用 useFilterMode 判斷
        if (useFilterMode.value) return true;
        // 其他模式：必須選擇至少一個會員
        return selectedMembers.value.length > 0;
      }
      return true;
    });

    // 下一步
    const nextStep = () => {
      if (!canProceedToNextStep.value) {
        return;
      }
      // 搜尋會員 / CSV 匯入模式：進入步驟二前，自動帶入 memberIds 走 filter 模式
      if (currentStep.value === 0 && ['search', 'csv'].includes(selectionMode.value) && selectedMembers.value.length > 0) {
        filterConfig.memberIds = selectedMembers.value.map(m => m.id);
        // 清除其他 filterConfig 條件，僅保留 memberIds
        filterConfig.tags = [];
        filterConfig.tiers = [];
        filterConfig.rfmSegments = [];
        filterConfig.rScore = [1, 5];
        filterConfig.fScore = [1, 5];
        filterConfig.mScore = [1, 5];
        filterConfig.minTotalSpent = null;
        filterConfig.maxTotalSpent = null;
        filterConfig.minPurchaseCount = null;
        filterConfig.maxPurchaseCount = null;
        filterConfig.registrationDateFrom = null;
        filterConfig.registrationDateTo = null;
        previewResult.value = {
          estimatedMemberCount: selectedMembers.value.length,
          previewMembers: selectedMembers.value.slice(0, 10)
        };
        useFilterMode.value = true;
      }
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    // 上一步
    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    // 標籤分類
    const tagCategories = computed(() => {
      const categories = {
        manual: {
          key: 'manual',
          label: '手動標籤',
          icon: 'ri-edit-line',
          tags: []
        },
        auto_rule: {
          key: 'auto_rule',
          label: '自動規則標籤',
          icon: 'ri-settings-3-line',
          tags: []
        }
      };

      availableTags.value.forEach(tag => {
        if (tag.tagSource === 'system') return;
        const source = tag.tagSource || 'manual';
        if (categories[source]) {
          categories[source].tags.push(tag);
        } else {
          categories.manual.tags.push(tag);
        }
      });

      return Object.values(categories).filter(cat => cat.tags.length > 0);
    });

    // 過濾後的標籤分類（根據搜尋關鍵字）
    const filteredTagCategories = computed(() => {
      if (!tagSearchQuery.value.trim()) {
        return tagCategories.value;
      }

      const query = tagSearchQuery.value.toLowerCase().trim();
      return tagCategories.value.map(category => ({
        ...category,
        tags: category.tags.filter(tag =>
          tag.tagName.toLowerCase().includes(query)
        )
      })).filter(category => category.tags.length > 0);
    });

    // 選擇方式切換
    const handleSelectionModeChange = (mode) => {
      selectionMode.value = mode;
    };

    // 會員搜尋
    const handleMemberSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      if (!memberSearchQuery.value || memberSearchQuery.value.trim().length < 2) {
        memberSearchResults.value = [];
        return;
      }

      searchTimeout.value = setTimeout(async () => {
        memberSearching.value = true;
        try {
          const response = await memberService.getMembers({
            search: memberSearchQuery.value.trim(),
            pageSize: 20
          });

          if (response && response.success && response.data) {
            memberSearchResults.value = response.data.items || [];
          } else {
            memberSearchResults.value = [];
          }
        } catch (err) {
          console.error('搜尋會員失敗:', err);
          memberSearchResults.value = [];
        } finally {
          memberSearching.value = false;
        }
      }, 500);
    };

    // 標籤搜尋
    const handleTagSearch = () => {
      // 即時過濾，不需要 API 呼叫
    };

    // 載入會員等級列表
    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({
          isActive: true,
          sortBy: 'displayOrder',
          order: 'asc'
        });
        if (response && response.success && response.data) {
          availableTiers.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (err) {
        console.error('載入會員等級失敗:', err);
        showError('載入會員等級失敗', err.message);
      } finally {
        tiersLoading.value = false;
      }
    };

    // 切換篩選區塊展開/收合
    const toggleFilterSection = (section) => {
      filterSectionsExpanded[section] = !filterSectionsExpanded[section];
    };

    // 切換篩選標籤
    const toggleFilterTag = (tagId) => {
      const index = filterConfig.tags.indexOf(tagId);
      if (index > -1) {
        filterConfig.tags.splice(index, 1);
      } else {
        filterConfig.tags.push(tagId);
      }
    };

    // 切換排除標籤
    const toggleExcludeTag = (tagId) => {
      const index = filterConfig.excludeTags.indexOf(tagId);
      if (index > -1) {
        filterConfig.excludeTags.splice(index, 1);
      } else {
        filterConfig.excludeTags.push(tagId);
      }
    };

    // 切換篩選等級
    const toggleFilterTier = (tierId) => {
      const index = filterConfig.tiers.indexOf(tierId);
      if (index > -1) {
        filterConfig.tiers.splice(index, 1);
      } else {
        filterConfig.tiers.push(tierId);
      }
    };

    // 預覽符合篩選條件的會員
    const previewFilteredMembers = async () => {
      previewing.value = true;
      try {
        // 準備 filterConfig
        const previewFilterConfig = { ...filterConfig };

        // 清理空值（包含預設的「不限制」值）
        const isDefaultRfmRange = (val) => Array.isArray(val) && val.length === 2 && val[0] === 1 && val[1] === 5;
        Object.keys(previewFilterConfig).forEach(key => {
          if (previewFilterConfig[key] === null || 
              (Array.isArray(previewFilterConfig[key]) && previewFilterConfig[key].length === 0) ||
              previewFilterConfig[key] === '') {
            delete previewFilterConfig[key];
          } else if (['rScore', 'fScore', 'mScore'].includes(key) && isDefaultRfmRange(previewFilterConfig[key])) {
            delete previewFilterConfig[key];
          }
        });

        const response = await manualIssuanceService.previewPointsIssuance({
          filterConfig: previewFilterConfig
        });

        if (response && response.success && response.data) {
          previewResult.value = response.data;
        } else {
          showError('預覽失敗', response?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('預覽失敗:', err);
        showError('預覽失敗', err.message || '請檢查篩選條件');
      } finally {
        previewing.value = false;
      }
    };

    // 使用篩選條件發放
    const useFilteredMembers = () => {
      if (!previewResult.value || previewResult.value.estimatedMemberCount === 0) {
        showError('沒有符合條件的會員');
        return;
      }
      useFilterMode.value = true;
      // 進入下一步設定點數
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    // 發放進度追蹤相關方法
    const startPollingProgress = (issuanceId) => {
      // 立即查詢一次
      checkIssuanceProgress(issuanceId);
      
      // 每 2 秒輪詢一次
      pollingInterval.value = setInterval(() => {
        checkIssuanceProgress(issuanceId);
      }, 2000);
    };

    const checkIssuanceProgress = async (issuanceId) => {
      try {
        const response = await manualIssuanceService.getPointsIssuanceResult(issuanceId);
        
        if (response && response.success && response.data) {
          const data = response.data;
          const targetCount = data.targetMemberCount || 0;
          const processedCount = (data.successCount || 0) + (data.failureCount || 0);
          const progressPercentage = targetCount > 0 
            ? Math.round((processedCount / targetCount) * 100) 
            : 0;
          
          // 計算預估剩餘時間
          let estimatedTimeRemaining = null;
          if (data.status === 'processing' && data.executedAt && processedCount > 0) {
            const elapsed = Date.now() - new Date(data.executedAt).getTime();
            const avgTimePerMember = elapsed / processedCount;
            const remaining = targetCount - processedCount;
            estimatedTimeRemaining = Math.ceil((remaining * avgTimePerMember) / 1000); // 轉換為秒
          }

          issuanceProgress.value = {
            issuanceId: data.issuanceId || data.id,
            status: data.status,
            targetMemberCount: targetCount,
            processedCount,
            successCount: data.successCount || 0,
            failureCount: data.failureCount || 0,
            totalPointsIssued: data.totalPointsIssued || 0,
            progressPercentage,
            estimatedTimeRemaining,
            executionTimeMs: data.executionTimeMs,
            errorMessage: data.errorMessage || null
          };

          // 如果已完成或失敗，停止輪詢
          if (data.status === 'completed' || data.status === 'failed') {
            stopPolling();
            
            if (data.status === 'completed') {
              showSuccess('發放完成', `成功發放給 ${data.successCount || 0} 位會員，共 ${formatNumber(data.totalPointsIssued || 0)} 點`);
            } else {
              showError('發放失敗', data.errorMessage || '發放過程中發生錯誤');
            }
          }
        }
      } catch (error) {
        console.error('查詢發放進度失敗:', error);
        // 不顯示錯誤，繼續輪詢
      }
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
    };

    const resetIssuance = () => {
      stopPolling();
      issuanceProgress.value = null;
      currentIssuanceId.value = null;
      useFilterMode.value = false;
      // 回到步驟一
      currentStep.value = 0;
    };

    const viewIssuanceDetail = () => {
      if (currentIssuanceId.value) {
        router.push(`/client/points/batch-issue/${currentIssuanceId.value}`);
      }
    };

    const getProgressStatusBadgeClass = (status) => {
      const classMap = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-warning'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getProgressStatusText = (status) => {
      const textMap = {
        pending: '等待中',
        processing: '處理中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return textMap[status] || status;
    };

    const formatEstimatedTime = (seconds) => {
      if (!seconds || seconds <= 0) return '計算中...';
      if (seconds < 60) return `${seconds} 秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes} 分 ${remainingSeconds} 秒` : `${minutes} 分`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours} 小時 ${remainingMinutes} 分` : `${hours} 小時`;
    };

    const formatExecutionTime = (ms) => {
      if (!ms) return '-';
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds} 秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes} 分 ${remainingSeconds} 秒` : `${minutes} 分`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours} 小時 ${remainingMinutes} 分` : `${hours} 小時`;
    };

    // 批量輸入處理
    const handleIdentifiersInput = () => {
      identifiersPreviewResult.value = null;
      resolvedIdentifierIds.value = [];
    };

    // 載入標籤
    const loadTags = async () => {
      tagsLoading.value = true;
      try {
        const response = await tagService.getTags();
        if (response && response.success && response.data) {
          availableTags.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (err) {
        console.error('載入標籤失敗:', err);
      } finally {
        tagsLoading.value = false;
      }
    };

    // 切換標籤選擇
    const toggleTag = (tagId) => {
      const index = selectedTagIds.value.indexOf(tagId);
      if (index > -1) {
        selectedTagIds.value.splice(index, 1);
      } else {
        selectedTagIds.value.push(tagId);
      }
    };

    // 預覽符合標籤的會員（後端處理，無數量上限）
    const previewMembersByTags = async () => {
      if (selectedTagIds.value.length === 0) return;

      tagPreviewing.value = true;
      tagPreviewResult.value = null;
      try {
        const previewFilterConfig = {
          tags: selectedTagIds.value,
          tagLogic: tagsTabMatchType.value
        };
        const response = await manualIssuanceService.previewPointsIssuance({
          filterConfig: previewFilterConfig
        });
        if (response && response.success && response.data) {
          tagPreviewResult.value = response.data;
        } else {
          showError('預覽失敗', response?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('預覽標籤會員失敗:', err);
        showError('預覽失敗', err.message || '請重試');
      } finally {
        tagPreviewing.value = false;
      }
    };

    // 使用標籤條件發放（後端動態處理全部符合會員）
    const useTagFilteredMembers = () => {
      if (!tagPreviewResult.value || tagPreviewResult.value.estimatedMemberCount === 0) {
        showError('沒有符合標籤的會員');
        return;
      }
      // 將標籤條件帶入 filterConfig，複用 filter 模式的提交流程
      filterConfig.tags = [...selectedTagIds.value];
      filterConfig.tagLogic = tagsTabMatchType.value;
      previewResult.value = tagPreviewResult.value;
      useFilterMode.value = true;
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    // 批量輸入：解析識別碼 → 預覽（後端處理，有進度追蹤）
    const previewMembersByIdentifiers = async () => {
      if (!identifiersText.value.trim()) return;

      identifiersPreviewing.value = true;
      identifiersPreviewResult.value = null;
      resolvedIdentifierIds.value = [];
      try {
        const identifiers = identifiersText.value
          .split(/[\n,，]/)
          .map(id => id.trim())
          .filter(id => id.length > 0)
          .slice(0, 10000);

        if (identifiers.length === 0) {
          showError('請輸入有效的識別資訊');
          return;
        }

        const resolveRes = await memberService.getMembersByIdentifiers(identifiers);
        const members = (resolveRes?.success && resolveRes.data)
          ? (resolveRes.data.items || resolveRes.data || [])
          : [];

        if (members.length === 0) {
          identifiersPreviewResult.value = { estimatedMemberCount: 0, previewMembers: [] };
          return;
        }

        const memberIds = members.map(m => m.id);
        resolvedIdentifierIds.value = memberIds;

        const previewRes = await manualIssuanceService.previewPointsIssuance({
          filterConfig: { memberIds }
        });

        if (previewRes?.success && previewRes.data) {
          identifiersPreviewResult.value = previewRes.data;
        } else {
          showError('預覽失敗', previewRes?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('批量輸入預覽失敗:', err);
        showError('預覽失敗', err.message || '請重試');
      } finally {
        identifiersPreviewing.value = false;
      }
    };

    // 使用批量輸入名單發放
    const useIdentifiersMembers = () => {
      if (!identifiersPreviewResult.value || identifiersPreviewResult.value.estimatedMemberCount === 0) {
        showError('沒有符合的會員');
        return;
      }
      filterConfig.memberIds = [...resolvedIdentifierIds.value];
      filterConfig.tags = [];
      filterConfig.tiers = [];
      previewResult.value = identifiersPreviewResult.value;
      useFilterMode.value = true;
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    // 根據識別資訊搜尋會員（保留以備相容，實際已改由 previewMembersByIdentifiers 處理）
    const searchMembersByIdentifiers = previewMembersByIdentifiers;

    // 切換會員選擇
    const toggleMember = (member) => {
      const index = selectedMembers.value.findIndex(m => m.id === member.id);
      if (index > -1) {
        selectedMembers.value.splice(index, 1);
      } else {
        selectedMembers.value.push(member);
      }
    };

    // 檢查會員是否已選擇
    const isMemberSelected = (memberId) => {
      return selectedMembers.value.some(m => m.id === memberId);
    };

    // 加入所有搜尋結果
    const addAllMembers = async () => {
      addingAll.value = true;
      try {
        const newMembers = memberSearchResults.value.filter(
          member => !isMemberSelected(member.id)
        );
        selectedMembers.value.push(...newMembers);
      } finally {
        addingAll.value = false;
      }
    };

    // 加入所有標籤搜尋結果
    const addAllTagMembers = () => {
      const newMembers = tagsSearchResults.value.filter(
        member => !isMemberSelected(member.id)
      );
      selectedMembers.value.push(...newMembers);
    };

    // 加入所有識別資訊搜尋結果
    const addAllIdentifierMembers = () => {
      const newMembers = identifiersSearchResults.value.filter(
        member => !isMemberSelected(member.id)
      );
      selectedMembers.value.push(...newMembers);
    };

    // 移除會員
    const removeMember = (memberId) => {
      const index = selectedMembers.value.findIndex(m => m.id === memberId);
      if (index > -1) {
        selectedMembers.value.splice(index, 1);
      }
    };

    // 清除所有會員
    const clearAllMembers = () => {
      selectedMembers.value = [];
    };

    // 驗證表單
    const validateForm = () => {
      errors.points = '';
      errors.reason = '';
      errors.memberIds = '';

      if (!useFilterMode.value && selectedMembers.value.length === 0) {
        errors.memberIds = '請至少選擇一位會員';
        return false;
      }

      if (!form.points || form.points <= 0) {
        errors.points = '點數必須大於 0';
        return false;
      }

      if (!form.reason || form.reason.trim().length === 0) {
        errors.reason = '請輸入發放原因';
        return false;
      }

      return true;
    };

    // 提交表單
    const handleSubmit = async () => {
      if (!validateForm()) {
        if (errors.memberIds) {
          currentStep.value = 0;
        }
        return;
      }

      submitting.value = true;
      submitError.value = '';

      // CSV 模式：先備份檔案至 S3（非阻塞式，失敗不影響發放）
      if (selectionMode.value === 'csv' && csvRef.value?.csvFile) {
        try {
          csvRef.value.setCsvUploadStatus('uploading');
          const { default: apiClient } = await import('@/api/axios.js');
          const formData = new FormData();
          formData.append('file', csvRef.value.csvFile);
          const uploadRes = await apiClient.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          csvRef.value.setCsvUploadStatus(uploadRes?.data?.fileUrl ? 'done' : 'error');
        } catch {
          csvRef.value.setCsvUploadStatus('error');
        }
      }

      try {
        let data;
        
        if (useFilterMode.value) {
          // 使用篩選條件發放
          const filterConfigForSubmit = { ...filterConfig };
          
          // 清理空值（包含預設的「不限制」值）
          const isDefaultRfmRange = (val) => Array.isArray(val) && val.length === 2 && val[0] === 1 && val[1] === 5;
          Object.keys(filterConfigForSubmit).forEach(key => {
            if (filterConfigForSubmit[key] === null || 
                (Array.isArray(filterConfigForSubmit[key]) && filterConfigForSubmit[key].length === 0) ||
                filterConfigForSubmit[key] === '') {
              delete filterConfigForSubmit[key];
            } else if (['rScore', 'fScore', 'mScore'].includes(key) && isDefaultRfmRange(filterConfigForSubmit[key])) {
              delete filterConfigForSubmit[key];
            }
          });

          data = {
            campaignName: form.reason || '批次發放點數',
            pointsAmount: form.points,
            pointsExpiryDays: form.effectiveDays || null,
            filterConfig: filterConfigForSubmit,
            sendNotification: false
          };
        } else {
          // 使用手動選擇的會員發放
          data = {
            points: form.points,
            reason: form.reason,
            filterConfig: {
              memberIds: selectedMembers.value.map(m => m.id)
            },
            sendNotification: false
          };
        }

        // 只有當 expiryDate 有值時才加入請求（僅手動模式）
        if (!useFilterMode.value && form.expiryDate && form.expiryDate.trim()) {
          data.expiryDate = form.expiryDate;
        }

        // 只有當 effectiveDays 有值時才加入請求（僅手動模式）
        if (!useFilterMode.value && form.effectiveDays !== null && form.effectiveDays !== undefined) {
          data.effectiveDays = form.effectiveDays;
        }

        let response;
        if (useFilterMode.value) {
          response = await manualIssuanceService.executePointsIssuance(data);
          if (response && response.success) {
            // 使用篩選條件模式：啟動進度追蹤
            const issuanceId = response.data?.issuanceId || response.data?.id;
            if (issuanceId) {
              currentIssuanceId.value = issuanceId;
              // 初始化進度資料
              issuanceProgress.value = {
                issuanceId,
                status: 'processing',
                targetMemberCount: previewResult.value?.estimatedMemberCount || 0,
                processedCount: 0,
                successCount: 0,
                failureCount: 0,
                totalPointsIssued: 0,
                progressPercentage: 0
              };
              // 開始輪詢進度
              startPollingProgress(issuanceId);
            } else {
              await showSuccess(
                '發放任務已建立',
                `將發放給 ${previewResult.value?.estimatedMemberCount || 0} 位符合條件的會員，每位 ${form.points} 點`
              );
              router.push('/client/points/batch-issue');
            }
          }
        } else {
          response = await pointsService.batchIssue(data);
          if (response && response.success) {
            await showSuccess(
              '批次發放成功',
              `成功發放給 ${selectedMembers.value.length} 位會員，每位 ${form.points} 點`
            );
            router.push('/client/points/batch-issue');
          }
        }
      } catch (err) {
        submitError.value = err.message || '批次發放失敗';
        showError('批次發放失敗', submitError.value);
      } finally {
        submitting.value = false;
      }
    };

    // 返回
    const handleBack = () => {
      router.push('/client/points/batch-issue');
    };

    onMounted(() => {
      loadClientMemberSystem();
      loadTags();
      loadTiers();
    });

    // 組件卸載時清理輪詢
    onBeforeUnmount(() => {
      stopPolling();
    });

    return {
      pageTitle,
      loading,
      error,
      submitting,
      submitError,
      currentStep,
      steps,
      selectionMode,
      memberSearchQuery,
      memberSearchResults,
      memberSearching,
      availableTags,
      selectedTagIds,
      tagsLoading,
      tagSearchQuery,
      tagsTabMatchType,
      tagPreviewResult,
      tagPreviewing,
      identifiersText,
      identifiersPreviewing,
      identifiersPreviewResult,
      resolvedIdentifierIds,
      selectedMembers,
      addingAll,
      form,
      errors,
      totalPoints,
      canProceedToNextStep,
      tagCategories,
      filteredTagCategories,
      handleSelectionModeChange,
      handleMemberSearch,
      handleTagSearch,
      handleIdentifiersInput,
      toggleTag,
      handleIdentifiersInput,
      previewMembersByIdentifiers,
      useIdentifiersMembers,
      previewMembersByTags,
      useTagFilteredMembers,
      toggleMember,
      isMemberSelected,
      addAllMembers,
      removeMember,
      clearAllMembers,
      nextStep,
      previousStep,
      handleSubmit,
      handleBack,
      // 篩選條件
      filterConfig,
      filterSectionsExpanded,
      availableTiers,
      tiersLoading,
      previewResult,
      previewing,
      useFilterMode,
      toggleFilterSection,
      toggleFilterTag,
      toggleExcludeTag,
      toggleFilterTier,
      previewFilteredMembers,
      useFilteredMembers,
      loadTiers,
      formatNumber,
      // 發放進度追蹤
      issuanceProgress,
      getProgressStatusBadgeClass,
      getProgressStatusText,
      formatEstimatedTime,
      formatExecutionTime,
      viewIssuanceDetail,
      resetIssuance,
      isWixtarClient,
      csvRef
    };
  }
};
</script>

<style scoped>
.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

/* 步驟導航樣式 */
.stepper {
  margin-bottom: 32px;
}

.stepper-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-item.disabled {
  opacity: 0.5;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 8px;
  transition: all 0.3s;
}

.step-item.active .step-number {
  background: #667eea;
  color: white;
}

.step-item.completed .step-number {
  background: #28a745;
  color: white;
}

.step-check {
  font-size: 20px;
}

.step-label {
  font-size: 14px;
  color: #6c757d;
  text-align: center;
}

.step-item.active .step-label {
  color: #667eea;
  font-weight: 600;
}

.stepper-line {
  position: relative;
  height: 2px;
  background: #e9ecef;
  margin-top: -20px;
  z-index: 0;
}

.stepper-progress {
  height: 100%;
  background: #667eea;
  transition: width 0.3s;
}

/* 表單樣式 */
.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.step-content {
  min-height: 400px;
}

.form-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

/* Tab 切換 */
.selection-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e9ecef;
}

.tab-button {
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: -2px;
}

.tab-button:hover {
  color: #667eea;
}

.tab-button.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

/* 會員搜尋 */
.member-search {
  position: relative;
}

.member-search i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.member-list {
  max-height: 400px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.member-item:hover {
  background: #f8f9fa;
}

.member-item.selected {
  background: #e7f3ff;
}

.member-item:last-child {
  border-bottom: none;
}

.member-info {
  flex: 1;
}

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-details {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.check-icon {
  color: #667eea;
  font-size: 20px;
}

.no-results {
  text-align: center;
  padding: 40px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

/* 標籤選擇 */
.tag-search-box {
  position: relative;
  margin-bottom: 16px;
}

.tag-search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.tag-category {
  margin-bottom: 24px;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.category-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-count {
  color: #999;
  font-size: 14px;
}

.tag-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 2px solid #e9ecef;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.tag-item:hover {
  border-color: #667eea;
  background: #f8f9fa;
}

.tag-item.selected {
  border-color: #667eea;
  background: #e7f3ff;
}

.tag-item .check-icon {
  margin-left: 4px;
}

/* 已選擇的會員 */
.selected-members-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #e9ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.selected-members-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px;
}

.selected-member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.selected-member-item:last-child {
  border-bottom: none;
}

/* 確認卡片 */
.confirmation-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.issue-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.summary-item.total {
  border-top: 2px solid #e9ecef;
  padding-top: 12px;
  margin-top: 8px;
}

.summary-item .label {
  color: #666;
  font-size: 14px;
}

.summary-item .value {
  color: #333;
  font-weight: 600;
  font-size: 16px;
}

.summary-item.total .value {
  color: #667eea;
  font-size: 18px;
}

/* 表單控制項 */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 12px;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

/* 按鈕 */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
  margin-top: 32px;
}

.step-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn-outline-secondary {
  background: white;
  color: #6c757d;
  border: 1px solid #6c757d;
}

.btn-outline-secondary:hover:not(:disabled) {
  background: #f8f9fa;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.btn-link:hover {
  color: #5568d3;
}

.text-danger {
  color: #dc3545;
}

/* 載入和錯誤狀態 */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc3545;
  margin-bottom: 16px;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.spin {
  animation: spin 1s linear infinite;
}

/* 篩選條件區塊 */
.filter-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-section-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.filter-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s;
}

.filter-section-header:hover {
  background: #e9ecef;
}

.filter-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.filter-section-header i.ri-arrow-down-s-line {
  transition: transform 0.3s;
}

.filter-section-header i.ri-arrow-down-s-line.rotated {
  transform: rotate(180deg);
}

.filter-section-body {
  padding: 20px;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.range-inputs input {
  flex: 1;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-range-inputs input {
  flex: 1;
}

.tier-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.tier-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.tier-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
  color: #1976d2;
}

/* 預覽結果 */
.filter-preview-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.preview-result-card {
  margin-top: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.preview-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.preview-count {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.preview-members-table {
  margin-bottom: 16px;
}

.preview-members-table table {
  width: 100%;
  border-collapse: collapse;
}

.preview-members-table th,
.preview-members-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.preview-members-table th {
  background: #fff;
  font-weight: 600;
  color: #333;
}

.preview-members-table tbody tr:hover {
  background: #f8f9fa;
}

.preview-note {
  margin-top: 12px;
  text-align: center;
  color: #666;
}

.preview-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.preview-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.preview-actions {
  margin-top: 16px;
  text-align: center;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

/* 發放進度追蹤 */
.issuance-progress-section {
  margin-bottom: 24px;
}

.progress-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.progress-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.progress-title i.spin {
  animation: spin 1s linear infinite;
}

.progress-bar-container {
  margin-bottom: 20px;
}

.progress-bar {
  width: 100%;
  height: 24px;
  background: #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
  border-radius: 12px;
}

.progress-text {
  margin-top: 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.progress-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #999;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.stat-item.success .stat-value {
  color: #28a745;
}

.stat-item.error .stat-value {
  color: #dc3545;
}

.progress-completed,
.progress-failed {
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
}

.progress-completed {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.progress-failed {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.success-message,
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
}

.success-message {
  color: #155724;
}

.error-message {
  color: #721c24;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
